import { useCallback, useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Settings2, RefreshCw, Sparkles, RotateCcw } from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { api } from '../../utils/api';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useRoleDashboard } from '../../hooks/useRoleDashboard';
import { WidgetGrid } from './widgets/WidgetGrid';
import { WidgetSelector } from './widgets/WidgetSelector';
import { WIDGET_REGISTRY } from './widgets/widgetRegistry';
import { resolveRoleKey, type DashboardRoleKey } from './roleLayouts';
import { AdminDashboard } from './roles/AdminDashboard';
import { SalesDashboard } from './roles/SalesDashboard';
import { AccountantDashboard } from './roles/AccountantDashboard';
import { InventoryDashboard } from './roles/InventoryDashboard';
import { ProductionDashboard } from './roles/ProductionDashboard';
import ProcurementDashboard from './roles/ProcurementDashboard';
import ProjectDashboard from './roles/ProjectDashboard';
// Apr 28, 2026 (Squad-D) — LeadManagerDashboard is no longer rendered here;
// /dashboard now redirects to /lead-manager for the LEAD_MANAGER role. The
// component file is kept (used by no one else) so existing imports elsewhere
// don't break; this can be deleted later once verified no other consumers.
// Executive dashboards (Apr 22, 2026 exec sprint)
import { SalesExecDashboard } from './executive/SalesExecDashboard';
import { ServiceTechDashboard } from './executive/ServiceTechDashboard';
import { ProductionOperatorDashboard } from './executive/ProductionOperatorDashboard';
import { WarehouseExecDashboard } from './executive/WarehouseExecDashboard';
import { AccountsExecDashboard } from './executive/AccountsExecDashboard';
import { FieldPartnerDashboard } from './executive/FieldPartnerDashboard';

/* Department-specific dashboard redirects (kept for pages that still have
 * dedicated manufacturing / warehouse dashboards). */
const DEPT_DASHBOARD_REDIRECT: Record<string, string> = {
  Manufacturing: '/manufacturing/dashboard',
  'Inventory & Warehouse': '/warehouse/dashboard',
  Inventory: '/warehouse/dashboard',
};

/* Apr 24, 2026 — Admin "View as" override.
 *
 * When an admin switches View-as in the TopBar, AppShell writes a SidebarView
 * string to sessionStorage. The Sidebar already honours it (that's how the
 * PRODUCTION / INVENTORY / etc. sidebar appears for admins). DashboardPage
 * previously ignored it — so `/dashboard` kept rendering the AdminDashboard
 * even when View-as = Production. That's now fixed.
 *
 * URL enforcement: SidebarViews that have a canonical dedicated dashboard URL
 * redirect to that URL so the address bar reflects the view. Views that share
 * the generic `/dashboard` render their matching role dashboard inline.
 */
const VIEW_OVERRIDE_KEY = 'aura-view-override';

const VIEW_DEDICATED_URL: Record<string, string> = {
  production: '/manufacturing/dashboard',
  inventory: '/warehouse/dashboard',
};

function readViewOverride(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(VIEW_OVERRIDE_KEY);
  } catch {
    return null;
  }
}

/**
 * Heuristic: is the signed-in user a Project / Delivery Manager?
 *
 * `DashboardRoleKey` in roleLayouts.ts does not model PROJECT_MANAGER yet, so
 * `resolveRoleKey()` cannot return it. We detect via designation/role name so
 * Project Managers land on the dedicated ProjectDashboard instead of the
 * legacy widget fallback. Pure UI — zero backend coupling.
 */
function isProjectManager(user: any): boolean {
  const title = (user?.designation ?? '').toLowerCase();
  const roleName = (user?.role?.name ?? '').toLowerCase();
  if (roleName === 'project_manager') return true;
  if (title.includes('project manager') || title.includes('delivery manager')) return true;
  return false;
}

/**
 * DashboardPage — role-based landing dashboard (router).
 *
 * Sprint A (Apr 22, 2026) replaced the monolithic widget grid with 7 role
 * dashboards owned by Dash-A/B/C squads. Pipeline:
 *
 *   1. Dept-redirect first (Manufacturing → /manufacturing/dashboard, etc.) —
 *      preserves legacy behaviour for dedicated dashboard routes.
 *   2. Project Manager heuristic → ProjectDashboard.
 *   3. resolveRoleKey(user) → DashboardRoleKey → branch to the matching
 *      role dashboard (Admin/Sales/Accountant/Inventory/Production/Procurement).
 *   4. Unknown role → LegacyWidgetDashboard (the original widget-grid
 *      implementation, preserved in-file as a fallback).
 *
 * Every role dashboard is UI-only and owns its own data hooks.
 */
export function DashboardPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // BUG-03 fix: subscribe to the custom event dispatched by AppShell.setViewOverride.
  // We use a ref counter so the component re-renders immediately on override change
  // without any page refresh. This avoids prop-drilling the viewOverride state all
  // the way from AppShell → TopBar → DashboardPage.
  const [, setViewVersion] = useState(0);
  useEffect(() => {
    const handler = () => setViewVersion((n) => n + 1);
    window.addEventListener('aura:view-override:change', handler);
    return () => window.removeEventListener('aura:view-override:change', handler);
  }, []);

  // Honour the existing dedicated-dashboard redirects BEFORE any role routing.
  useEffect(() => {
    const deptName = (user as any)?.department?.name;
    if (deptName && DEPT_DASHBOARD_REDIRECT[deptName]) {
      navigate(DEPT_DASHBOARD_REDIRECT[deptName], { replace: true });
    }
  }, [user, navigate]);

  // Apr 24, 2026 — Admin View-as override takes precedence over resolveRoleKey.
  // If admin picks "View: Production" in TopBar, /dashboard must render the
  // Production view (and ideally redirect to /manufacturing/dashboard so the
  // URL reflects the view — URL enforcement).
  // BUG-03: now re-reads from sessionStorage on every render (triggered by the
  // event above) so the correct dashboard renders immediately after switching.
  const viewOverride = readViewOverride();
  if (viewOverride) {
    const dedicated = VIEW_DEDICATED_URL[viewOverride];
    if (dedicated) {
      return <Navigate to={dedicated} replace />;
    }
    switch (viewOverride) {
      case 'owner':
        return <AdminDashboard />;
      case 'sales':
        return <SalesDashboard />;
      case 'accountant':
        return <AccountantDashboard />;
      case 'procurement':
        return <ProcurementDashboard />;
      case 'project':
        return <ProjectDashboard />;
      case 'lead_manager':
        // Apr 28, 2026 (Squad-D) — Lead Manager has a dedicated 12-screen home.
        return <Navigate to="/lead-manager" replace />;
      case 'sales_executive':
      case 'telecaller':
      case 'backend_staff':
        return <SalesExecDashboard />;
      case 'service_technician':
        return <ServiceTechDashboard />;
      case 'production_operator':
        return <ProductionOperatorDashboard />;
      case 'warehouse_executive':
        return <WarehouseExecDashboard />;
      case 'accounts_executive':
        return <AccountsExecDashboard />;
      case 'field_partner':
        return <FieldPartnerDashboard />;
      // Unknown override → fall through to default resolveRoleKey branch below.
    }
  }

  // Project Manager short-circuit (role key not in DashboardRoleKey enum).
  if (isProjectManager(user)) {
    return <ProjectDashboard />;
  }

  // Map resolved DashboardRoleKey → role dashboard component.
  // Apr 26, 2026 (Squad-B) — Squad-A added LEAD_MANAGER + BACKEND_STAFF to
  // DashboardRoleKey. Belt-and-braces fallback via raw role.name kept for
  // any tenant whose seed has those role rows but resolveRoleKey doesn't
  // map them yet (no harm — switch below also catches them).
  const roleKey: DashboardRoleKey = resolveRoleKey(user as any);
  const rawRoleName = ((user as any)?.role?.name ?? '').toString().toLowerCase().trim();
  // Apr 28, 2026 (Squad-D) — Lead Manager redirected to dedicated home.
  if (rawRoleName === 'lead_manager') return <Navigate to="/lead-manager" replace />;
  if (rawRoleName === 'backend_staff' || rawRoleName === 'backend_crm' || rawRoleName === 'tele_sales') {
    return <SalesExecDashboard />;
  }

  switch (roleKey) {
    case 'OWNER':
      return <AdminDashboard />;
    case 'LEAD_MANAGER':
      // Apr 28, 2026 (Squad-D) — Lead Manager has its own dedicated home.
      return <Navigate to="/lead-manager" replace />;
    case 'BACKEND_STAFF':
      return <SalesExecDashboard />;

    case 'SALES_HEAD':
    case 'SALES_MANAGER':
      return <SalesDashboard />;

    // Sales Executive + Telecaller → SalesExecDashboard.
    // (Apr 24, 2026 — SALES_EXEC_FIELD + SALES_EXEC_OFFICE merged into SALES_EXECUTIVE.)
    case 'SALES_EXECUTIVE':
    case 'TELECALLER':
      return <SalesExecDashboard />;

    case 'FINANCE_MANAGER':
      return <AccountantDashboard />;

    case 'PRODUCTION_HEAD':
      return <ProductionDashboard />;

    // Production Supervisor / Operator → ProductionOperatorDashboard
    case 'PRODUCTION_SUPERVISOR':
      return <ProductionOperatorDashboard />;

    case 'INVENTORY_HEAD':
      return <InventoryDashboard />;

    case 'PURCHASE_HEAD':
      return <ProcurementDashboard />;

    // Service Technician → ServiceTechDashboard
    case 'SERVICE_TECHNICIAN':
      return <ServiceTechDashboard />;

    // Warehouse Executive → WarehouseExecDashboard (auto-routes from role/designation)
    case 'WAREHOUSE_EXECUTIVE':
      return <WarehouseExecDashboard />;

    // Accounts Executive → AccountsExecDashboard (auto-routes from role/designation)
    case 'ACCOUNTS_EXECUTIVE':
      return <AccountsExecDashboard />;

    // Field Partner (installer / channel partner) → FieldPartnerDashboard
    case 'FIELD_PARTNER':
      return <FieldPartnerDashboard />;

    // SERVICE_MANAGER — no dedicated role dashboard component shipped in this
    // sprint. Fall through to the legacy widget view so it still renders.
    default:
      return <LegacyWidgetDashboard />;
  }
}

/* ---------------------------------------------------------------------- */
/*  Legacy widget-based dashboard (fallback for unmapped roles)            */
/* ---------------------------------------------------------------------- */

/**
 * The original widget-grid dashboard that shipped before Sprint A.
 * Preserved verbatim and used as a safety net when `resolveRoleKey()`
 * returns a role we do not yet have a dedicated dashboard for
 * (Service Manager, Service Technician, Telecaller).
 */
function LegacyWidgetDashboard() {
  const { user } = useAuthStore();

  const {
    roleLayout,
    enabledWidgetIds,
    layouts,
    loading: layoutLoading,
    isCustom,
    handleLayoutChange,
    toggleWidget,
    resetToDefault,
  } = useRoleDashboard();

  const { data, loading: widgetDataLoading, error, refetch } = useWidgetData();
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [isCustomizing, setIsCustomizing] = useState(false);

  useEffect(() => {
    api
      .get('/credits/balance')
      .then((res) => {
        if (res.data?.data?.balance != null) setCreditBalance(res.data.data.balance);
      })
      .catch(() => {});
  }, []);

  const openCustomizer = useCallback(() => setIsCustomizing(true), []);
  const closeCustomizer = useCallback(() => setIsCustomizing(false), []);

  // Widgets the registry actually knows how to render — silently drop
  // unknowns so the grid doesn't render blank tiles.
  const knownWidgetIds = new Set(WIDGET_REGISTRY.map((w) => w.id));
  const renderableIds = enabledWidgetIds.filter((id) => knownWidgetIds.has(id));
  const allowedIds = new Set(knownWidgetIds);

  const firstName = (user as any)?.firstName ?? '';
  const subtitleRendered =
    firstName && roleLayout.subtitle.length > 0
      ? `${firstName} — ${roleLayout.subtitle}`
      : roleLayout.subtitle;

  return (
    <div className="min-h-full">
      {/* Header — reflects the role layout's title + subtitle */}
      <div className="mb-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{roleLayout.title}</h1>
          <p className="mt-1 text-sm text-gray-500">{subtitleRendered}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refetch}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
            title="Refresh data"
          >
            <RefreshCw size={14} className={widgetDataLoading ? 'animate-spin' : ''} />
          </button>
          {isCustom && (
            <button
              onClick={() => void resetToDefault()}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
              title="Reset to default role layout"
            >
              <RotateCcw size={14} />
              Reset
            </button>
          )}
          <button
            onClick={openCustomizer}
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            style={{ background: 'var(--ink-0)', color: 'var(--ivory-0)', border: '1px solid var(--ink-0)' }}
          >
            <Settings2 size={16} />
            Customize
          </button>
        </div>
      </div>

      {/* AI Credit Balance Banner */}
      {creditBalance != null && (
        <Link
          to="/credits"
          className="mb-4 flex flex-col items-start gap-2 rounded-lg border border-blue-500/20 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 px-4 py-2.5 transition-colors hover:from-blue-500/10 hover:to-indigo-500/10 sm:flex-row sm:items-center"
        >
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-blue-400" />
            <span className="text-sm font-medium text-[var(--text-secondary)]">
              AI Credits:{' '}
              <span className="font-semibold text-blue-400">
                {creditBalance.toLocaleString('en-IN')}
              </span>{' '}
              available
            </span>
          </div>
          <span className="mt-1 flex sm:mt-0 sm:ml-auto text-xs text-[var(--text-muted)] hover:underline">
            View details &rarr;
          </span>
        </Link>
      )}

      {/* Error banner */}
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
          <button onClick={refetch} className="ml-2 font-medium text-red-800 underline">
            Retry
          </button>
        </div>
      )}

      {/* Loading spinner on first paint while we resolve persisted layout */}
      {layoutLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
        </div>
      ) : (
        <WidgetGrid
          enabledWidgetIds={renderableIds}
          layouts={layouts}
          data={data}
          loading={widgetDataLoading}
          onLayoutChange={handleLayoutChange}
        />
      )}

      {/* Empty state */}
      {!layoutLoading && renderableIds.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-16">
          <Settings2 size={40} className="mb-3 text-gray-300" />
          <p className="text-sm text-gray-500">No widgets enabled for {roleLayout.title}</p>
          <button
            onClick={openCustomizer}
            className="mt-2 text-sm font-medium text-[var(--ink-0)] hover:underline"
          >
            Customize your dashboard
          </button>
        </div>
      )}

      {/* Widget Selector slide-over */}
      {isCustomizing && (
        <WidgetSelector
          enabledIds={enabledWidgetIds}
          allowedIds={allowedIds}
          onToggle={(id) => void toggleWidget(id)}
          onReset={() => void resetToDefault()}
          onClose={closeCustomizer}
        />
      )}
    </div>
  );
}
